import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ExclamationTriangleIcon,
  CloseBoldIcon,
  CloseIcon,
  Input,
  OverlayTriggerTooltip,
  ImageIcon,
  RepromptIcon,
  SerifXIcon,
} from '@utilities';

import { IMAGE_UPLOAD_MAX_FILE_SIZE } from '@api/instant_survey';

import { createImageObject, uploadImageToS3 } from '@api/instant_survey';

import styles from './_index.module.scss';

const ConceptDetails = ({ group, onUpdate }) => {
  const [imageError, setImageError] = useState(null);
  const [image, setImage] = useState(null);
  const [isEditingVariable, setIsEditingVariable] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [variableName, setVariableName] = useState(group?.conceptName || '');

  const fileInputRef = useRef(null);
  const inputWrapperRef = useRef(null);

  useEffect(() => {
    if (!isEditingVariable) return;
    const handleClickOutside = (e) => {
      if (
        isEditingVariable &&
        inputWrapperRef.current &&
        !inputWrapperRef.current.contains(e.target)
      ) {
        if (!variableName.trim()) setIsEditingVariable(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingVariable, variableName]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > IMAGE_UPLOAD_MAX_FILE_SIZE) {
      setImageError({ errorMsg: 'File size exceeds 5MB limit' });
      return;
    }

    setImageError(null);
    setIsImageLoading(true);

    try {
      const imageObject = await createImageObject({ objectType: file.type.substring(6) });

      if (!imageObject.url.startsWith('http://s3mock')) {
        await uploadImageToS3({ file, url: imageObject.url });
      }

      onUpdate({ conceptImage: { id: imageObject.id } });
      setImage(file);
    } catch (error) {
      setImageError({ errorMsg: 'please try again.' });
      console.error('Error uploading image:', error);
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleFileRemove = () => {
    onUpdate({ conceptImage: null });
    setImage(null);
  };

  const handleInputChange = (value) => {
    setVariableName(value);
    onUpdate({ conceptName: value });
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={styles['concept-details']}>
      <h4>Concept Details</h4>
      <div className={styles['concept-details-input-image-container']}>
        {!image && !group?.conceptImage && !isImageLoading && !imageError ? (
          <div
            aria-label="Add Image"
            className={styles['concept-details-image']}
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClick();
            }}
            role="button"
            tabIndex={0}
          >
            <ImageIcon />
            <p>
              Add an <strong>Image</strong>
            </p>
            <input
              accept="image/gif, image/jpeg, image/png"
              className={styles['concept-details-input-file']}
              disabled={isImageLoading}
              hidden
              id="concept-image-input"
              onChange={handleFileChange}
              ref={fileInputRef}
              type="file"
            />
          </div>
        ) : (
          <>
            {imageError && (
              <div className={styles['concept-details-input-image-error']}>
                <div className={styles['concept-details-input-image-error-text']}>
                  <ExclamationTriangleIcon /> Image upload failed: {imageError.errorMsg}
                </div>
                <div
                  className={styles['concept-details-input-image-error-close-icon']}
                  onClick={() => setImageError(null)}
                >
                  <CloseBoldIcon />
                </div>
              </div>
            )}

            {isImageLoading && (
              <div className={styles['concept-details-input-image-loading']}>
                <RepromptIcon /> Image is uploading...
              </div>
            )}

            {!isImageLoading && !imageError && (image || group?.conceptImage?.cdnUrl) && (
              <div className={styles['concept-details-input-image-preview']}>
                <img
                  alt="Uploaded File"
                  src={image ? URL.createObjectURL(image) : group?.conceptImage?.cdnUrl}
                />
                <div className={styles['concept-details-input-remove-image-container']}>
                  <OverlayTriggerTooltip content="Remove Image" position="bottom">
                    <div
                      className={styles['concept-details-input-remove-image-button']}
                      onClick={handleFileRemove}
                    >
                      <CloseIcon className={styles['concept-details-input-remove-image-icon']} />
                    </div>
                  </OverlayTriggerTooltip>
                </div>
                <div className={styles['concept-details-input-image-preview-overlay']} />
              </div>
            )}
          </>
        )}
      </div>

      <div className={styles['concept-details-variable']}>
        {isEditingVariable || group?.conceptName ? (
          <div className={styles['concept-details-variable-input']} ref={inputWrapperRef}>
            <SerifXIcon />
            <Input
              autoFocus
              className={styles['concept-details-input-field']}
              maxLength={128}
              onChange={(value) => handleInputChange(value)}
              placeholder="Enter Name"
              value={variableName}
            />
          </div>
        ) : (
          <div
            onClick={() => setIsEditingVariable(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setIsEditingVariable(true);
            }}
            role="button"
            tabIndex={0}
          >
            <SerifXIcon />
            <span>
              Add a <strong>Name</strong>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

ConceptDetails.propTypes = {
  group: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default ConceptDetails;
